import React, { useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiEye, FiEyeOff, FiUser, FiLock, FiAlertCircle } from 'react-icons/fi';

const LoginContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 420px;
  background: white;
  padding: 30px;
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 20px;
    padding: 2px;
    background: linear-gradient(135deg, rgba(255, 0, 0, 0.2), rgba(255, 0, 0, 0.1));
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }
`;

const Logo = styled(motion.div)`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #ff0000 0%, #ff4444 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 16px;
  text-align: center;
  line-height: 1.2;
  margin-bottom: 30px;
  box-shadow: 0 8px 16px rgba(255, 0, 0, 0.2);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    border: 2px solid rgba(255, 0, 0, 0.3);
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.1);
      opacity: 0;
    }
    100% {
      transform: scale(1);
      opacity: 0.8;
    }
  }
`;

const Title = styled(motion.h1)`
  font-size: 28px;
  margin-bottom: 10px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  color: #333;
`;

const Subtitle = styled(motion.p)`
  font-size: 16px;
  color: #666;
  margin-bottom: 40px;
  font-family: 'Poppins', sans-serif;
  text-align: center;
`;

const Form = styled.form`
  width: 100%;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 20px;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  display: flex;
  align-items: center;
  font-size: 18px;
  z-index: 1;
  transition: all 0.2s ease;
`;

const Input = styled(motion.input)`
  width: 100%;
  padding: 15px;
  padding-left: 45px;
  border: 2px solid #eee;
  border-radius: 12px;
  font-size: 16px;
  font-family: 'Poppins', sans-serif;
  outline: none;
  transition: all 0.3s ease;
  background: #f8f9fa;
  
  &:focus {
    border-color: #ff0000;
    box-shadow: 0 0 0 3px rgba(255, 0, 0, 0.1);
    background: white;
  }

  &:focus + ${InputIcon} {
    color: #ff0000;
  }
`;

const PasswordInput = styled(Input)`
  padding-right: 45px;
`;

const EyeIcon = styled(motion.button)`
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  display: flex;
  align-items: center;
  font-size: 20px;
  
  &:hover {
    color: #ff0000;
  }
`;

const ValidationMessage = styled(motion.p)`
  color: #ff0000;
  font-size: 12px;
  margin-top: 5px;
  font-family: 'Poppins', sans-serif;
  display: flex;
  align-items: center;
  gap: 5px;
  
  svg {
    font-size: 14px;
  }
`;

const LoginButton = styled(motion.button)`
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, #ff0000 0%, #ff4444 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 20px;
  font-family: 'Poppins', sans-serif;
  box-shadow: 0 4px 10px rgba(255, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  &:disabled {
    background: linear-gradient(135deg, #ffcccc 0%, #ffdddd 100%);
    cursor: not-allowed;
    box-shadow: none;
  }

  &:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 15px rgba(255, 0, 0, 0.3);
  }
`;

const VersionText = styled.div`
  margin-top: 20px;
  font-size: 12px;
  color: #999;
  text-align: center;
  font-family: 'Poppins', sans-serif;
`;

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const Login = () => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/home');
  };

  const handleUsernameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
  };

  const isValidPassword = password.length >= 8 && 
    /[a-zA-Z]/.test(password) && 
    /\d/.test(password);

  return (
    <LoginContainer
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <MainContainer>
        <Logo
          variants={itemVariants}
          whileHover={{ scale: 1.1, rotate: 360 }}
          transition={{ duration: 0.5 }}
        >
          STYLE<br />BOX
        </Logo>
        
        <Title variants={itemVariants}>Welcome</Title>
        <Subtitle variants={itemVariants}>Please enter your login credentials to continue</Subtitle>
        
        <Form onSubmit={handleSubmit}>
          <InputWrapper>
            <InputIcon>
              <FiUser />
            </InputIcon>
            <Input
              type="text"
              placeholder="Username"
              value={username}
              onChange={handleUsernameChange}
              variants={itemVariants}
              whileFocus={{ scale: 1.01 }}
            />
          </InputWrapper>
          
          <InputWrapper>
            <InputIcon>
              <FiLock />
            </InputIcon>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={handlePasswordChange}
              variants={itemVariants}
              whileFocus={{ scale: 1.01 }}
            />
            <EyeIcon
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </EyeIcon>
          </InputWrapper>
          
          {password && !isValidPassword && (
            <ValidationMessage
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <FiAlertCircle />
              At least 8 characters, containing a letter and a number
            </ValidationMessage>
          )}
          
          <LoginButton
            type="submit"
            disabled={!username || !isValidPassword}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            variants={itemVariants}
          >
            Login
          </LoginButton>

          <VersionText>Version 1.0.1</VersionText>
        </Form>
      </MainContainer>
    </LoginContainer>
  );
};

export default Login; 